import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { segmentTrack } from 'flow/actions/segment-actions';
import { sendMessage } from 'flow/actions/tickets-action';
import Button from 'flow/components/ui-components/Button';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import Skeleton from 'flow/components/ui-components/Skeleton';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useAutosizeTextArea from 'flow/hooks/useAutosizeTextArea';
import { TicketKey } from 'flow/types/EntityKeys';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import { twMerge } from 'tailwind-merge';
import RobotIcon from '../RobotIcon';

interface Props {
    summaryContext: string;
    ticketKey: TicketKey;
    isFetching?: boolean;
    summaryOriginal?: string;
}

const TicketSummaryItem = ({
    summaryContext,
    isFetching,
    ticketKey,
    summaryOriginal,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { closeModal } = useModal();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [hasStartedEditing, setHasStartedEditing] = useState(false);

    const [summary, setSummary] = useState('');

    useEffect(() => {
        setSummary(summaryOriginal);
    }, [ticketKey, summaryOriginal]);

    useAutosizeTextArea(textareaRef, summary);

    const handleFocusTextarea = () => {
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    };

    const handleSend = () => {
        dispatch(
            segmentTrack(TICKET_STRINGS.POST_INTERNAL_NOTE_CLICK, {
                component: 'AI Summary Modal',
            }),
        );
        const textForInputField =
            summaryContext === 'TRANSCRIPT'
                ? `${i18next.t('ai.summary.call')}\n\n${summary}`
                : `${i18next.t('ai.summary.conversation')}\n\n${summary}`;
        dispatch(sendMessage(ticketKey, textForInputField));
        closeModal();
    };

    const handleTextareaChange = (event: any) => {
        setSummary(event.target.value);

        if (!hasStartedEditing) {
            dispatch(
                segmentTrack(TICKET_STRINGS.START_TEXTAREA_EDIT, {
                    component: 'AI Summary Modal',
                }),
            );
            setHasStartedEditing(true);
        }
    };

    return (
        <>
            <div className="flex flex-col px-[26px] pt-[26px]">
                <div
                    className={twMerge(
                        'flex h-6 gap-x-3',
                        isFetching ? 'animate-fade' : '',
                    )}
                >
                    <div
                        className={twMerge(
                            'size-6 fill-gray-600',
                            isFetching ? 'animate-spinSmoothAcceleration' : '',
                        )}
                    >
                        <RobotIcon />
                    </div>
                    <p className="flex items-end text-sm font-bold">
                        {isFetching
                            ? i18next.t('ai.summary.generating')
                            : summaryContext === 'TRANSCRIPT'
                              ? `${i18next.t('ai.summary.call')}`
                              : `${i18next.t('ai.summary.conversation')}`}
                    </p>
                </div>
                <div className="flex flex-col px-9">
                    {!isFetching ? (
                        <textarea
                            ref={textareaRef}
                            className="mt-2 block w-full resize-none overflow-hidden rounded-sm bg-transparent p-2 text-base text-gray-800 hover:bg-gray-200 focus:bg-gray-200 focus:outline-hidden"
                            aria-label={i18next.t('ai.summary.summary')}
                            value={summary}
                            onChange={handleTextareaChange}
                            onClick={() => {
                                dispatch(
                                    segmentTrack(
                                        TICKET_STRINGS.FOCUS_TEXTAREA_EDIT,
                                        {
                                            component: 'AI Summary Modal',
                                        },
                                    ),
                                );
                            }}
                        />
                    ) : (
                        <div className="mt-4 rounded-sm bg-gray-100 p-2">
                            <Skeleton>
                                <div className="flex flex-1 flex-col gap-2 py-1">
                                    <Skeleton.Text className="h-3 w-96" />
                                    <Skeleton.Text className="h-3 w-52" />
                                    {
                                        <div>
                                            <div className="mt-4 flex h-9 w-28 items-center justify-center rounded-sm bg-gray-200">
                                                <div className="h-3 w-3/4 rounded-sm bg-gray-300" />
                                            </div>
                                        </div>
                                    }
                                </div>
                            </Skeleton>
                        </div>
                    )}
                </div>
            </div>
            <footer className="sticky bottom-0 flex justify-end gap-2 px-[26px] py-6">
                {isFetching ? (
                    <div className="my-0.5 h-8" />
                ) : (
                    <>
                        <IconButton
                            onClick={() => {
                                dispatch(
                                    segmentTrack(
                                        TICKET_STRINGS.CLICK_EDIT_SUMMARY_BUTTON,
                                        {
                                            component: 'AI Summary Modal',
                                        },
                                    ),
                                );
                                handleFocusTextarea();
                            }}
                            tooltipLabel={i18next.t('Tooltip.Nav.Title.Edit')}
                            className="bg-gray-200 p-2"
                        >
                            <Icon iconKey="edit" />
                        </IconButton>
                        <Button
                            className="bg-gray-200 p-2"
                            onClick={handleSend}
                            disabled={isFetching}
                        >
                            <p className="text-sm text-gray-600">
                                {i18next.t('tickets.internal.note.post_as')}
                            </p>
                        </Button>
                    </>
                )}
            </footer>
        </>
    );
};

export default TicketSummaryItem;
